/**
 * Connect-4 engine (WS-17a). Pure, side-effect-free, exhaustively unit tested — the only
 * thing that knows what a legal Connect-4 game looks like.
 *
 * **CRDT-friendly move log.** The collaborative state a `game:<id>` doc holds is a single
 * append-only `Y.Array<Connect4Move>` (see `GAME_MOVES_KEY`) — never a mutable board. Each
 * move carries the column *and* the id of the player who dropped it, because Yjs's total
 * merge order (not wall-clock time) decides what "first" means once two offline clients
 * reconnect, and a bare column index has no way to reject a move that shouldn't have
 * happened. `deriveConnect4` replays the array in that merged order and is the *only*
 * authority on whose turn it is: it never trusts a move's position in the array to already
 * be legal, it re-derives turn parity from the moves it has already accepted.
 *
 * This is what makes the "both players moved while offline" case harmless: each client
 * pushes its move locally (client-side validates first, so it never happens in the honest
 * single-writer case), but if a race means two moves both claim the same turn slot after
 * merge, `deriveConnect4` accepts whichever one appears first in the merged array and
 * silently rejects the other — the loser's client re-derives the same board everyone else
 * sees and its move simply isn't on it. No error, no conflict UI: replay order is the only
 * truth. See `packages/shared/test/games.connect4.test.ts` for the merge-race test.
 */
export const CONNECT4_COLS = 7;
export const CONNECT4_ROWS = 6;
function createEmptyBoard() {
    return Array.from({ length: CONNECT4_ROWS }, () => Array(CONNECT4_COLS).fill(null));
}
function inBounds(row, col) {
    return row >= 0 && row < CONNECT4_ROWS && col >= 0 && col < CONNECT4_COLS;
}
const WIN_DIRECTIONS = [
    [0, 1], // horizontal
    [1, 0], // vertical
    [1, 1], // diagonal, top-left to bottom-right
    [1, -1], // diagonal, top-right to bottom-left
];
/**
 * Checks whether the piece just placed at `(row, col)` completes a 4-in-a-row in any
 * direction. Pure function of the board — usable standalone, and what `deriveConnect4`
 * calls after every placement.
 */
export function winCheck(board, row, col, player) {
    if (board[row]?.[col] !== player)
        return null;
    for (const [dr, dc] of WIN_DIRECTIONS) {
        const run = [[row, col]];
        let r = row + dr;
        let c = col + dc;
        while (inBounds(r, c) && board[r]?.[c] === player) {
            run.push([r, c]);
            r += dr;
            c += dc;
        }
        r = row - dr;
        c = col - dc;
        while (inBounds(r, c) && board[r]?.[c] === player) {
            run.unshift([r, c]);
            r -= dr;
            c -= dc;
        }
        if (run.length >= 4) {
            const placedIndex = run.findIndex(([rr, cc]) => rr === row && cc === col);
            const start = Math.max(0, Math.min(placedIndex, run.length - 4));
            return { cells: run.slice(start, start + 4), playerIndex: player };
        }
    }
    return null;
}
/** Columns a piece can legally be dropped into right now (top cell still empty). */
export function legalMoves(board) {
    const out = [];
    for (let c = 0; c < CONNECT4_COLS; c += 1) {
        if (board[0]?.[c] === null)
            out.push(c);
    }
    return out;
}
/** Every column full — draw iff true and nobody has already won. */
export function isDraw(board) {
    return legalMoves(board).length === 0;
}
/**
 * Replays `moves` in order against an empty board, validating each one before it counts:
 * right column range, column not full, game not already decided, and — the CRDT-merge
 * safeguard — made by whoever's turn parity says it should be. An invalid move is skipped
 * (not applied, not counted, doesn't advance whose turn is next) and replay continues with
 * the next one, so a single stray/duplicate move never derails the rest of the game.
 */
export function deriveBoard(moves, playerIds) {
    const board = createEmptyBoard();
    const heights = Array(CONNECT4_COLS).fill(0);
    const rejectedMoveIndexes = [];
    let turnIndex = 0;
    let winner = null;
    for (let i = 0; i < moves.length; i += 1) {
        const move = moves[i];
        if (!move || winner) {
            rejectedMoveIndexes.push(i);
            continue;
        }
        const expectedPlayerId = playerIds[turnIndex];
        const isRightPlayer = move.playerId === expectedPlayerId;
        const isRightColumn = Number.isInteger(move.col) && move.col >= 0 && move.col < CONNECT4_COLS;
        const hasRoom = isRightColumn && (heights[move.col] ?? 0) < CONNECT4_ROWS;
        if (!isRightPlayer || !isRightColumn || !hasRoom) {
            rejectedMoveIndexes.push(i);
            continue;
        }
        const height = heights[move.col] ?? 0;
        const row = CONNECT4_ROWS - 1 - height;
        const boardRow = board[row];
        if (!boardRow) {
            rejectedMoveIndexes.push(i);
            continue;
        }
        boardRow[move.col] = turnIndex;
        heights[move.col] = height + 1;
        winner = winCheck(board, row, move.col, turnIndex);
        turnIndex = turnIndex === 0 ? 1 : 0;
    }
    const draw = !winner && isDraw(board);
    return {
        board,
        appliedMoves: moves.length - rejectedMoveIndexes.length,
        rejectedMoveIndexes,
        turn: winner || draw ? null : turnIndex,
        winner,
        isDraw: draw,
    };
}
/** Alias kept for callers reading this file top-down: `deriveConnect4` is the same function
 * as `deriveBoard`, named to match its sibling `winCheck`/`legalMoves`/`isDraw`. */
export const deriveConnect4 = deriveBoard;
//# sourceMappingURL=connect4.js.map